import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { Bell, Check, Loader2, MessageSquare, Star, Calendar, AlertOctagon } from "lucide-react";

const ICON = {
  message: MessageSquare,
  booking_status: Calendar,
  review: Star,
  waitlist: Calendar,
  sos: AlertOctagon,
};

const TONE = {
  sos: "text-danger",
  review: "text-ochre-600",
  message: "text-sage-700",
  booking_status: "text-sage-700",
  waitlist: "text-ochre-600",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data.results); setUnread(data.unread);
    } catch (e) { console.warn("[NotificationBell] load failed", e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, []);

  useEffect(() => {
    const onClick = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    try { await api.post("/notifications/mark-all-read"); load(); } catch (e) { console.warn("[NotificationBell] markAll failed", e); }
  };
  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch (e) { console.warn("[NotificationBell] markOne failed", e); }
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell"
        className="relative p-2 rounded-full text-stone2-700 hover:bg-stone2-100 transition-colors duration-200">
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span data-testid="notification-unread-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto paper-card shadow-lg z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone2-200">
            <span className="font-heading font-semibold text-sage-900 text-sm">Notifications</span>
            {unread > 0 && (
              <button type="button" onClick={markAll} data-testid="notification-mark-all-read"
                className="text-xs text-sage-700 hover:text-sage-900 font-semibold">Mark all read</button>
            )}
          </div>
          {loading && <div className="p-4 text-sm text-stone2-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-stone2-500">You're all caught up ✨</div>
          )}
          <ul>
            {items.map((n) => {
              const Icon = ICON[n.type] || Bell;
              const tone = TONE[n.type] || "text-sage-700";
              const content = (
                <div className={`flex gap-3 px-4 py-3 border-b border-stone2-100 last:border-0 hover:bg-stone2-50 transition-colors duration-200 ${n.read ? "opacity-70" : ""}`}>
                  <Icon className={`w-4 h-4 mt-0.5 ${tone}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-sage-900 truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-stone2-600 mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-stone2-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-sage-600 mt-1.5" />}
                </div>
              );
              return (
                <li key={n.id} onClick={() => markOne(n.id)} data-testid={`notification-item-${n.id}`}>
                  {n.link ? <Link to={n.link} onClick={() => setOpen(false)}>{content}</Link> : content}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
